/**
 * KioskSettingsTab - Admin panel for kiosk configuration
 * Manage idle timeout, categories, receipt settings, and appearance
 */
import { useState, useEffect } from 'react';
import { 
  Monitor, Clock, Tag, Receipt, Palette, Volume2, 
  Save, RotateCcw, RefreshCw, Settings, Eye, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function KioskSettingsTab({ authHeaders }) {
  const [settings, setSettings] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState('general');

  useEffect(() => {
    fetchSettings();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default settings to use when API fails or returns empty
  const DEFAULT_KIOSK_SETTINGS = {
    idle_timeout_seconds: 120,
    show_prices: true,
    allowed_categories: [],
    blocked_categories: [],
    require_customer_name: false,
    allow_special_instructions: true,
    max_items_per_order: 20,
    welcome_message: "Welcome to Bulaccino Cafe",
    thank_you_message: "Vinaka! Your order has been placed",
    print_receipt: true,
    receipt_header: "Bulaccino Cafe",
    receipt_footer: "Thank you for your order!",
    show_order_number_large: true,
    theme: "dark",
    logo_url: null,
    background_color: null,
    accent_color: null,
    play_sounds: true,
    order_confirmation_sound: true,
    accessibility_mode: false,
    large_text_size: "normal",
    touch_target_size: "normal",
    high_contrast: false
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/kiosk/settings');
      setSettings(res.data.settings || DEFAULT_KIOSK_SETTINGS);
      setAvailableCategories(res.data.available_categories || []);
    } catch (err) {
      console.warn('Failed to fetch kiosk settings:', err.message, '- Using defaults');
      setSettings(DEFAULT_KIOSK_SETTINGS);
      setAvailableCategories([]);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/kiosk/stats?days=7');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/kiosk/settings', settings);
      toast.success('Kiosk settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const resetSettings = async () => {
    if (!confirm('Reset all kiosk settings to defaults?')) return;
    
    try {
      const res = await api.post('/admin/kiosk/settings/reset');
      setSettings(res.data.settings);
      toast.success('Settings reset to defaults');
    } catch (err) {
      toast.error('Failed to reset settings');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleCategory = (category, type) => {
    const listKey = type === 'allowed' ? 'allowed_categories' : 'blocked_categories';
    const currentList = settings[listKey] || [];
    
    if (currentList.includes(category)) {
      updateSetting(listKey, currentList.filter(c => c !== category));
    } else {
      updateSetting(listKey, [...currentList, category]);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sections = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'categories', label: 'Categories', icon: Tag },
    { id: 'accessibility', label: 'Accessibility', icon: Eye },
    { id: 'receipt', label: 'Receipt', icon: Receipt },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'sounds', label: 'Sounds', icon: Volume2 },
    { id: 'stats', label: 'Statistics', icon: BarChart3 }
  ];

  return (
    <div className="space-y-6" data-testid="kiosk-settings-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
            <Monitor className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Kiosk Settings</h2>
            <p className="text-muted-foreground text-sm">Configure self-service kiosk behavior</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetSettings}
            className="rounded-full"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="rounded-full"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sections.map(section => {
          const Icon = section.icon;
          return (
            <Button
              key={section.id}
              variant={activeSection === section.id ? 'default' : 'outline'}
              onClick={() => setActiveSection(section.id)}
              className="rounded-full whitespace-nowrap"
            >
              <Icon className="w-4 h-4 mr-2" />
              {section.label}
            </Button>
          );
        })}
      </div>

      {/* General Settings */}
      {activeSection === 'general' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">General Settings</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Idle Timeout (seconds)
              </label>
              <Input
                type="number"
                min="30"
                max="600"
                value={settings.idle_timeout_seconds}
                onChange={(e) => updateSetting('idle_timeout_seconds', parseInt(e.target.value))}
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Reset kiosk after this many seconds of inactivity (30-600)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Max Items Per Order
              </label>
              <Input
                type="number"
                min="1"
                max="50"
                value={settings.max_items_per_order}
                onChange={(e) => updateSetting('max_items_per_order', parseInt(e.target.value))}
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum items allowed in a single order
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Welcome Message
              </label>
              <Input
                value={settings.welcome_message}
                onChange={(e) => updateSetting('welcome_message', e.target.value)}
                className="bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Thank You Message
              </label>
              <Input
                value={settings.thank_you_message}
                onChange={(e) => updateSetting('thank_you_message', e.target.value)}
                className="bg-background"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_prices}
                onChange={(e) => updateSetting('show_prices', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Show Prices</div>
                <div className="text-xs text-muted-foreground">Display prices on menu items</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.require_customer_name}
                onChange={(e) => updateSetting('require_customer_name', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Require Name</div>
                <div className="text-xs text-muted-foreground">Customer must enter name</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.allow_special_instructions}
                onChange={(e) => updateSetting('allow_special_instructions', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Special Instructions</div>
                <div className="text-xs text-muted-foreground">Allow order notes</div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Categories Settings */}
      {activeSection === 'categories' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">Category Visibility</h3>
          <p className="text-sm text-muted-foreground">
            Choose which menu categories to show or hide on the kiosk. Leave empty to show all categories.
          </p>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-3">Available Categories</h4>
              <div className="flex flex-wrap gap-2">
                {availableCategories.map(cat => {
                  const isAllowed = settings.allowed_categories?.length === 0 || 
                                   settings.allowed_categories?.includes(cat);
                  const isBlocked = settings.blocked_categories?.includes(cat);
                  
                  return (
                    <div key={cat} className="flex items-center gap-1">
                      <button
                        onClick={() => toggleCategory(cat, 'allowed')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${
                          isAllowed && !isBlocked
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-2 border-green-500'
                            : 'bg-muted/30 text-muted-foreground border-2 border-transparent'
                        }`}
                      >
                        {cat.replace(/-/g, ' ')}
                      </button>
                      <button
                        onClick={() => toggleCategory(cat, 'blocked')}
                        className={`p-2 rounded-full ${
                          isBlocked 
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        title={isBlocked ? 'Unblock' : 'Block'}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <strong>How it works:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Green categories are shown on the kiosk</li>
                  <li>Click the ✕ to block a category completely</li>
                  <li>If "Allowed Categories" is empty, all non-blocked categories are shown</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accessibility Settings */}
      {activeSection === 'accessibility' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">Accessibility Settings</h3>
          <p className="text-sm text-muted-foreground">
            Make the kiosk easier to use for customers with visual or motor impairments.
          </p>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.accessibility_mode || false}
                onChange={(e) => updateSetting('accessibility_mode', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Enable Accessibility Mode by Default</div>
                <div className="text-xs text-muted-foreground">Start kiosk with larger text and touch targets enabled</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.high_contrast || false}
                onChange={(e) => updateSetting('high_contrast', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">High Contrast Mode</div>
                <div className="text-xs text-muted-foreground">Increase contrast for better visibility</div>
              </div>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Text Size
              </label>
              <select
                value={settings.large_text_size || 'normal'}
                onChange={(e) => updateSetting('large_text_size', e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="normal">Normal</option>
                <option value="large">Large (125%)</option>
                <option value="extra-large">Extra Large (150%)</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Increase text size for better readability
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Touch Target Size
              </label>
              <select
                value={settings.touch_target_size || 'normal'}
                onChange={(e) => updateSetting('touch_target_size', e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="normal">Normal (56px min)</option>
                <option value="large">Large (72px min)</option>
                <option value="extra-large">Extra Large (88px min)</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Increase button sizes for easier tapping
              </p>
            </div>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800">
            <div className="text-sm text-green-700 dark:text-green-300">
              <strong>Note:</strong> Customers can also toggle accessibility mode directly on the kiosk 
              by tapping the "Aa" button in the header. This allows individual customization without 
              changing the default settings for all users.
            </div>
          </div>
        </div>
      )}

      {/* Receipt Settings */}
      {activeSection === 'receipt' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">Receipt Settings</h3>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.print_receipt}
                onChange={(e) => updateSetting('print_receipt', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Print Receipt</div>
                <div className="text-xs text-muted-foreground">Automatically print receipt after order</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_order_number_large}
                onChange={(e) => updateSetting('show_order_number_large', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Large Order Number</div>
                <div className="text-xs text-muted-foreground">Show order number prominently on confirmation</div>
              </div>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Receipt Header
              </label>
              <Input
                value={settings.receipt_header}
                onChange={(e) => updateSetting('receipt_header', e.target.value)}
                placeholder="Business name for receipt"
                className="bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Receipt Footer
              </label>
              <Input
                value={settings.receipt_footer}
                onChange={(e) => updateSetting('receipt_footer', e.target.value)}
                placeholder="Thank you message"
                className="bg-background"
              />
            </div>
          </div>

          {/* Receipt Preview */}
          <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-border max-w-xs mx-auto font-mono text-sm">
            <div className="text-center border-b border-dashed border-gray-300 dark:border-gray-700 pb-2 mb-2">
              <div className="font-bold">{settings.receipt_header || 'Bulaccino Cafe'}</div>
              <div className="text-xs text-muted-foreground">Order #1234</div>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span>1x Cappuccino</span>
                <span>$5.50</span>
              </div>
              <div className="flex justify-between">
                <span>1x Croissant</span>
                <span>$4.00</span>
              </div>
            </div>
            <div className="border-t border-dashed border-gray-300 dark:border-gray-700 pt-2 mt-2">
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>$9.50</span>
              </div>
            </div>
            <div className="text-center text-xs text-muted-foreground mt-2 pt-2 border-t border-dashed border-gray-300 dark:border-gray-700">
              {settings.receipt_footer || 'Thank you!'}
            </div>
          </div>
        </div>
      )}

      {/* Appearance Settings */}
      {activeSection === 'appearance' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">Appearance</h3>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Theme</label>
            <div className="flex gap-4">
              <button
                onClick={() => updateSetting('theme', 'dark')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  settings.theme === 'dark'
                    ? 'border-primary bg-gray-900 text-white'
                    : 'border-border bg-muted/30'
                }`}
              >
                <div className="text-center">
                  <div className="w-12 h-8 mx-auto mb-2 rounded bg-gray-800 border border-gray-700"></div>
                  <div className="font-medium">Dark</div>
                  <div className="text-xs text-muted-foreground">Easier on the eyes</div>
                </div>
              </button>
              
              <button
                onClick={() => updateSetting('theme', 'light')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  settings.theme === 'light'
                    ? 'border-primary bg-white'
                    : 'border-border bg-muted/30'
                }`}
              >
                <div className="text-center">
                  <div className="w-12 h-8 mx-auto mb-2 rounded bg-gray-100 border border-gray-300"></div>
                  <div className="font-medium text-foreground">Light</div>
                  <div className="text-xs text-muted-foreground">Brighter display</div>
                </div>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Logo URL (Optional)
              </label>
              <Input
                value={settings.logo_url || ''}
                onChange={(e) => updateSetting('logo_url', e.target.value)}
                placeholder="https://..."
                className="bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Accent Color (Optional)
              </label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={settings.accent_color || '#22c55e'}
                  onChange={(e) => updateSetting('accent_color', e.target.value)}
                  className="w-14 h-10 p-1 bg-background"
                />
                <Input
                  value={settings.accent_color || ''}
                  onChange={(e) => updateSetting('accent_color', e.target.value)}
                  placeholder="#22c55e"
                  className="bg-background flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sound Settings */}
      {activeSection === 'sounds' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <h3 className="font-heading text-lg text-foreground">Sound Settings</h3>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.play_sounds}
                onChange={(e) => updateSetting('play_sounds', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className="font-medium text-foreground">Enable Sounds</div>
                <div className="text-xs text-muted-foreground">Play sounds for interactions</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings.order_confirmation_sound}
                onChange={(e) => updateSetting('order_confirmation_sound', e.target.checked)}
                disabled={!settings.play_sounds}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <div className={`font-medium ${!settings.play_sounds ? 'text-muted-foreground' : 'text-foreground'}`}>
                  Order Confirmation Sound
                </div>
                <div className="text-xs text-muted-foreground">Play sound when order is placed</div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Statistics */}
      {activeSection === 'stats' && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg text-foreground">Kiosk Statistics (Last 7 Days)</h3>
            <Button variant="outline" size="sm" onClick={fetchStats} className="rounded-full">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {stats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-2xl font-bold text-foreground">{stats.total_orders}</div>
                  <div className="text-sm text-muted-foreground">Kiosk Orders</div>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ${stats.total_revenue?.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">Revenue</div>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-2xl font-bold text-foreground">
                    ${stats.avg_order_value?.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg Order</div>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="text-2xl font-bold text-foreground">{stats.total_items_sold}</div>
                  <div className="text-sm text-muted-foreground">Items Sold</div>
                </div>
              </div>

              {stats.popular_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-3">Popular Kiosk Items</h4>
                  <div className="space-y-2">
                    {stats.popular_items.map((item) => (
                      <div key={`popular-${item.name}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-foreground">{item.name}</span>
                        <span className="font-medium text-foreground">{item.quantity} sold</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No kiosk statistics available</p>
            </div>
          )}
        </div>
      )}

      {/* Preview Link */}
      <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl border border-cyan-200 dark:border-cyan-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg text-foreground">Test Your Kiosk</h3>
            <p className="text-sm text-muted-foreground">Open the kiosk in a new tab to see your changes</p>
          </div>
          <Button
            onClick={() => window.open('/kiosk', '_blank')}
            className="rounded-full"
          >
            <Eye className="w-4 h-4 mr-2" />
            Open Kiosk
          </Button>
        </div>
      </div>
    </div>
  );
}
